import { SelfServiceError } from '@/Services/BKAPI/Errors/SelfServiceError';
import {
    CANCELLATION_OPTION_TYPE,
    CancellationOption,
    CancellationOptionType,
    FLEX_TICKET_STATUS,
    ORDER_ITEM_TYPE,
    OrderDetails,
    OrderItem,
    TICKET_STATUS,
    TicketHolderDataProperty,
    TicketHolderFormField,
} from '@/Services/BKAPI/models';
import { toOrderType } from '@/Services/BKAPI/Reducers/OrderReducer';
import { SelfServiceOrderResponse } from '@bookingkit/api-v4';
import type { SelfServiceOrderResponseOrderItemsInner } from '@bookingkit/api-v4/models/self-service-order-response-order-items-inner';
import type { SelfServiceTicketHolderInformationItemResponse } from '@bookingkit/api-v4/models/self-service-ticket-holder-information-item-response';

const mapTicketHolderInfo = (
    fields: SelfServiceTicketHolderInformationItemResponse[] = []
): TicketHolderFormField[] => {
    const validFields = new Set<TicketHolderDataProperty>([
        'firstName',
        'lastName',
        'email',
        'zip',
        'country',
        'dateOfBirth',
        'city',
        'photo',
        'streetName',
        'streetNumber',
    ]);
    return (fields ?? [])
        .map((field) => {
            const name = field?.name as TicketHolderDataProperty | undefined;
            if (!name || !validFields.has(name)) {
                return null;
            }
            return { name, required: Boolean(field?.required) };
        })
        .filter((field): field is TicketHolderFormField => field !== null);
};

type WireRefundOption = { id?: string };
type WireCancellationOption = {
    id?: string;
    type?: string;
    refund_options?: WireRefundOption[];
    restrictions?: {
        validity_period?: { start_at?: string; end_at?: string };
    };
};

const VALID_CANCELLATION_TYPES = new Set<string>(
    Object.values(CANCELLATION_OPTION_TYPE)
);

export const mapCancellationOptions = (raw: unknown): CancellationOption[] => {
    if (!Array.isArray(raw)) {
        return [];
    }
    return (raw as WireCancellationOption[])
        .map((option): CancellationOption | null => {
            if (!option?.id || !VALID_CANCELLATION_TYPES.has(option.type ?? '')) {
                return null;
            }
            const start = option.restrictions?.validity_period?.start_at;
            const end = option.restrictions?.validity_period?.end_at;
            if (!start || !end) {
                return null;
            }
            const refundOptions = (option.refund_options ?? [])
                .filter((ro): ro is { id: string } => typeof ro?.id === 'string')
                .map((ro) => ({ id: ro.id }));
            return {
                id: option.id,
                type: option.type as CancellationOptionType,
                restrictions: {
                    validityPeriod: {
                        startAt: new Date(start),
                        endAt: new Date(end),
                    },
                },
                refundOptions,
            };
        })
        .filter((option): option is CancellationOption => option !== null);
};

const reduceOrderItems = (
    orderItems: SelfServiceOrderResponseOrderItemsInner[]
): OrderItem[] => {
    return (
        orderItems
            ?.filter(
                (item) => item.order_item_type === ORDER_ITEM_TYPE.SEASON_PASS
            )
            .map((item, index) => {
                const orderItemId = item.id;
                if (orderItemId == null) {
                    throw SelfServiceError.fromMissingProperty(
                        `order_items[${index}].id`,
                        'string',
                        orderItemId
                    );
                }
                if (item.tickets == null || item.tickets.length === 0) {
                    throw SelfServiceError.fromMissingProperty(
                        `order_items[${index}].tickets`,
                        'object',
                        item.tickets
                    );
                }
                const orderItemStatus =
                    item.status === 'ACCEPTED'
                        ? FLEX_TICKET_STATUS.ACTIVE
                        : FLEX_TICKET_STATUS.CANCELLED;
                const validTicketStatuses = new Set<string>(
                    Object.values(TICKET_STATUS)
                );
                const tickets = item.tickets.map((x, ticketIndex) => {
                    const path = `order_items[${index}].tickets[${ticketIndex}]`;
                    if (!x.id || typeof x.id !== 'string') {
                        throw SelfServiceError.fromMissingProperty(
                            `${path}.id`,
                            'string',
                            x.id
                        );
                    }
                    if (!x.code || typeof x.code !== 'string') {
                        throw SelfServiceError.fromMissingProperty(
                            `${path}.code`,
                            'string',
                            x.code
                        );
                    }
                    if (!x.status || !validTicketStatuses.has(x.status)) {
                        throw SelfServiceError.fromMissingProperty(
                            `${path}.status`,
                            `one of ${[...validTicketStatuses].join(', ')}`,
                            x.status
                        );
                    }
                    return {
                        code: x.code,
                        id: x.id,
                        status: x.status as (typeof TICKET_STATUS)[keyof typeof TICKET_STATUS],
                    };
                });
                return {
                    type: toOrderType(item.order_item_type),
                    id: item.id!,
                    name: (item.offer_name ?? '') as string,
                    status: orderItemStatus,
                    restrictions: {
                        ticketHolderInfo: mapTicketHolderInfo(
                            item.restrictions?.ticket_holder_information as []
                        ),
                    },
                    tickets,
                    cancellationOptions: mapCancellationOptions(
                        item.cancellation_options
                    ),
                };
            }) ?? []
    );
};

export const fromAPIToOrderDetails = (
    orderResponse: SelfServiceOrderResponse
): OrderDetails => {
    const { id, code, locale, order_items } = orderResponse;
    if (id == null) {
        throw SelfServiceError.fromMissingProperty('id', 'string', undefined);
    }
    if (code == null) {
        throw SelfServiceError.fromMissingProperty('code', 'string', undefined);
    }
    if (order_items == null) {
        throw SelfServiceError.fromMissingProperty(
            'order_items',
            'array',
            undefined
        );
    }

    return {
        id: id,
        code: code,
        locale: locale as any,
        orderItems: reduceOrderItems(order_items),
    };
};
